import React, { useMemo, useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { CheckIcon, AlertTriangleIcon, MinusIcon, PlusIcon, ChevronRightIcon, TruckIcon, ShieldCheckIcon, StoreIcon, ChevronLeftIcon } from 'lucide-react';
interface SizeOption {
  value: string;
  label: string;
  range: string;
}
const SIZES: SizeOption[] = [{
  value: 'xxxs',
  label: 'XXX-Small',
  range: '210-215mm'
}, {
  value: 'xxs',
  label: 'XX-Small',
  range: '220-225mm'
}, {
  value: 'xs',
  label: 'X-Small',
  range: '230-235mm'
}, {
  value: 's',
  label: 'Small',
  range: '240-245mm'
}, {
  value: 'm',
  label: 'Medium',
  range: '250-255mm'
}, {
  value: 'l',
  label: 'Large',
  range: '260-265mm'
}, {
  value: 'xl',
  label: 'X-Large',
  range: '270-275mm'
}, {
  value: 'xxl',
  label: 'XX-Large',
  range: '280-285mm'
}, {
  value: 'xxxl',
  label: 'XXX-Large',
  range: '290-295mm'
}, {
  value: 'xxxxl',
  label: 'XXXX-Large',
  range: '300-305mm'
}];
const FEATURES = ['Additional sensors added for improved accuracy with E-Headgear.', 'Improved design for more robust and accurate detection.', 'To be used with Gen3 Electronic Protector E-Hogu only.', 'Official Daedo Gen3 sensor sock for competition and training.'];
const GALLERY = ['https://cdn.magicpatterns.com/uploads/oEAFMK42eSCha1JbDjXzPu/image.png'];
const RELATED = [{
  name: 'Daedo Shin Guard',
  image: 'https://drwzpk38qkpfb.cloudfront.net/core/getthumb?site=9848&user=1373&w=400&h=400&file=uploaded%2Fimages%2FWacoku%2FWTF%20B167.jpg800x800.jpg',
  href: '#wacoku-products'
}, {
  name: 'Daedo Forearm Guard',
  image: 'https://drwzpk38qkpfb.cloudfront.net/core/getthumb?site=9848&user=1373&w=400&h=400&file=uploaded%2Fimages%2FWacoku%2FWTF%20B166.jpg800x800.jpg',
  href: '#wacoku-products'
}, {
  name: 'Daedo WT/WTF TKD Gloves',
  image: 'https://drwzpk38qkpfb.cloudfront.net/core/getthumb?site=9848&user=1373&w=400&h=400&file=uploaded%2Fimages%2FWacoku%2FWTF%20B124W.jpg800x800.jpg',
  href: '#wacoku-products'
}];
const SIZING_TOP = {
  header: ['mm (KOR)', '195', '200', '205', '210', '215', '220', '225', '230', '235', '240', '245'],
  rows: [['ZA 2020', '26', '27', '28', '29', '30', '31', '32', '33', '34', '35', '36'], ['ZA 2021', '26', '27', '28', '29', '30', '31', '32', '33', '34', '35', '36'], ['ZA 2024', '', '', '', '', '', '', '', '', '35', '36', '37'], ['ZA 3010', '', '', '', '', '', '', '', '', '35', '36', '37'], ['ZA 3020', '', '', '', '', '', '', '', '', '35', '36', '37'], ['PRO 15944', '', '', '', '', '', 'XXS (35)', 'XXS (35)', 'XXS (36-37)', 'XXS (36-37)', 'S (38)', 'S (38)'], ['PRITF 2022', '', '', '', '', '', '', '', 'XXS', 'XXS', 'XS', 'XS']]
};
const SIZING_BOTTOM = {
  header: ['mm (KOR)', '250', '255', '260', '265', '270', '275', '280', '285', '290', '295', '300'],
  rows: [['ZA 2020', '37', '38', '39', '40', '41', '42', '43', '44', '45', '46', '47'], ['ZA 2021', '37', '38', '39', '40', '41', '42', '43', '44', '45', '46', '47'], ['ZA 2024', '38', '39', '40', '41', '42', '43', '44', '45', '46', '', ''], ['ZA 3010', '38', '39', '40', '41', '42', '43', '44', '45', '46', '', ''], ['ZA 3020', '38', '39', '40', '41', '42', '43', '44', '45', '46', '', ''], ['PRO 15944', 'M (39-40)', 'M (39-40)', 'L (41)', 'L (41)', 'XL (42-43)', 'XL (42-43)', 'XXL (44)', 'XXL (44)', 'XXXL (45-46)', 'XXXL (45-46)', 'XXXXL (47-48)'], ['PRITF 2022', 'XS', 'XS', 'XS', 'XS', 'S', 'S', 'M', 'M', 'L', 'L', 'XL']]
};
type TabId = 'description' | 'sizing' | 'related' | 'shipping';
const BASE_PRICE = 110.84;
const ENGRAVING_PRICE_PER_PAIR = 10;
export function Gen3EFootGearPage() {
  const [activeImage, setActiveImage] = useState(0);
  // Multi-size quantities — keyed by size.value, default 0
  const [quantities, setQuantities] = useState<Record<string, number>>(() => {
    const initial: Record<string, number> = {};
    SIZES.forEach(s => {
      initial[s.value] = 0;
    });
    return initial;
  });
  const [engravingEnabled, setEngravingEnabled] = useState(false);
  const [nameMode, setNameMode] = useState<'same' | 'different'>('same');
  const [sameName, setSameName] = useState('');
  const [perPairNames, setPerPairNames] = useState<Record<string, string[]>>({});
  const [activeTab, setActiveTab] = useState<TabId>('description');
  const totalPairs = useMemo(() => Object.values(quantities).reduce((sum, n) => sum + n, 0), [quantities]);
  const productSubtotal = totalPairs * BASE_PRICE;
  const engravingTotal = engravingEnabled ? totalPairs * ENGRAVING_PRICE_PER_PAIR : 0;
  const grandTotal = productSubtotal + engravingTotal;
  const setSizeQty = (value: string, qty: number) => {
    setQuantities(prev => ({
      ...prev,
      [value]: Math.max(0, qty)
    }));
  };
  const incrementSize = (value: string) => {
    setSizeQty(value, (quantities[value] || 0) + 1);
  };
  const decrementSize = (value: string) => {
    setSizeQty(value, (quantities[value] || 0) - 1);
  };
  const updatePerPairName = (sizeValue: string, pairIdx: number, val: string) => {
    setPerPairNames(prev => {
      const arr = prev[sizeValue] ? [...prev[sizeValue]] : [];
      arr[pairIdx] = val;
      return {
        ...prev,
        [sizeValue]: arr
      };
    });
  };
  return <div className="min-h-screen bg-white flex flex-col font-sans text-gray-900" data-id="element-654">
      <Header data-id="element-655" />

      <main className="flex-grow pt-32 pb-20 bg-gray-50" data-id="element-656">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-657">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6" data-id="element-658">
            <a href="#" className="hover:text-gray-900" data-id="element-659">
              Home
            </a>
            <ChevronRightIcon className="w-4 h-4" data-id="element-660" />
            <a href="#gen3" className="hover:text-gray-900" data-id="element-661">
              Gen 3
            </a>
            <ChevronRightIcon className="w-4 h-4" data-id="element-662" />
            <span className="text-gray-900 font-medium" data-id="element-663">Gen3 E-Foot Gear</span>
          </nav>

          {/* Product Hero */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start" data-id="element-664">
            {/* Left: Gallery */}
            <div className="lg:sticky lg:top-32" data-id="element-665">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-10 flex items-center justify-center aspect-square" data-id="element-666">
                <img src={GALLERY[activeImage]} alt="Gen3 E-Foot Gear" className="max-h-full max-w-full object-contain transition-transform duration-300 hover:scale-105" data-id="element-667" />
              </div>
              {GALLERY.length > 1 && <div className="flex gap-3 mt-4 justify-center lg:justify-start" data-id="element-668">
                  {GALLERY.map((src, idx) => <button key={idx} onClick={() => setActiveImage(idx)} className={`w-20 h-20 rounded-xl border-2 p-2 bg-white transition-all ${activeImage === idx ? 'border-red-600 shadow-md' : 'border-gray-200 hover:border-gray-400'}`} aria-label={`View image ${idx + 1}`} data-id="element-669">
                      <img src={src} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-contain" data-id="element-670" />
                    </button>)}
                </div>}
            </div>

            {/* Right: Purchase Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8" data-id="element-671">
              {/* Category */}
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-blue-700 bg-blue-50 px-2.5 py-1 rounded-full mb-3" data-id="element-672">
                Gen 3
              </span>

              {/* Title */}
              <h1 className="text-3xl md:text-4xl font-black text-red-600 leading-tight mb-3" data-id="element-673">
                Gen3 E-Foot Gear
              </h1>

              {/* Grouped description */}
              <div className="mb-6" data-id="element-674">
                <p className="text-base text-gray-600 mb-4" data-id="element-675">
                  The official Daedo Gen3 sensor sock — built for accurate
                  detection in competition and serious training with Gen3
                  E-Hogus.
                </p>

                <ul className="space-y-2.5 mb-4" data-id="element-676">
                  {FEATURES.map((feature, idx) => <li key={idx} className="flex items-start gap-2.5 text-sm text-gray-700" data-id="element-677">
                      <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" data-id="element-678" />
                      <span data-id="element-679">{feature}</span>
                    </li>)}
                </ul>

                <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4" data-id="element-680">
                  <AlertTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-681" />
                  <p className="text-sm font-bold text-red-700 uppercase tracking-wide leading-tight" data-id="element-682">
                    Do not purchase unless you know you are using Gen 3 E-Hogus
                  </p>
                </div>
              </div>

              {/* Price */}
              <div className="flex items-baseline gap-3 mb-6 pb-6 border-b border-gray-100" data-id="element-683">
                <span className="text-4xl font-black text-gray-900" data-id="element-684">
                  ${BASE_PRICE.toFixed(2)}
                </span>
                <span className="text-sm text-gray-500" data-id="element-685">USD per pair</span>
              </div>

              {/* Choose Sizes & Quantities */}
              <div className="mb-6" data-id="element-686">
                <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap" data-id="element-687">
                  <h2 className="text-base font-bold text-gray-900" data-id="element-688">
                    Choose Sizes & Quantities
                  </h2>
                  <button type="button" onClick={() => setActiveTab('sizing')} className="text-xs text-blue-600 hover:text-blue-800 underline" data-id="element-689">
                    Not sure? View suggested sizing chart
                  </button>
                </div>

                <div className="rounded-xl border border-gray-200 overflow-hidden divide-y divide-gray-100" data-id="element-690">
                  {SIZES.map(s => <SizeRow key={s.value} size={s} qty={quantities[s.value] || 0} onIncrement={() => incrementSize(s.value)} onDecrement={() => decrementSize(s.value)} onChange={val => setSizeQty(s.value, val)} data-id="element-691" />)}
                </div>
              </div>

              {/* Order Summary */}
              <div className="rounded-xl bg-gray-50 border border-gray-100 p-4 mb-6" data-id="element-692">
                <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider" data-id="element-693">
                  Order Summary
                </h3>
                <div className="space-y-2 text-sm" data-id="element-694">
                  <div className="flex justify-between" data-id="element-695">
                    <span className="text-gray-600" data-id="element-696">Selected pairs</span>
                    <span className="font-semibold text-gray-900" data-id="element-697">
                      {totalPairs}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-698">
                    <span className="text-gray-600" data-id="element-699">Product total</span>
                    <span className="font-semibold text-gray-900" data-id="element-700">
                      ${productSubtotal.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-701">
                    <span className="text-gray-600" data-id="element-702">Laser engraving</span>
                    <span className="font-semibold text-gray-900" data-id="element-703">
                      {engravingEnabled ? `+$${engravingTotal.toFixed(2)}` : 'Not added'}
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 mt-2 border-t border-gray-200" data-id="element-704">
                    <span className="font-bold text-gray-900" data-id="element-705">Total</span>
                    <span className="font-black text-lg text-gray-900" data-id="element-706">
                      ${grandTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Personalization */}
              <div className="mb-6" data-id="element-707">
                <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider" data-id="element-708">
                  Personalization
                </h3>

                <label className="flex items-start gap-3 cursor-pointer p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors" data-id="element-709">
                  <input type="checkbox" checked={engravingEnabled} onChange={e => setEngravingEnabled(e.target.checked)} className="mt-0.5 w-5 h-5 accent-red-600 cursor-pointer" data-id="element-710" />
                  <span className="flex-1" data-id="element-711">
                    <span className="block text-sm font-semibold text-gray-900" data-id="element-712">
                      Add name laser engraving{' '}
                      <span className="text-red-600" data-id="element-713">
                        (+${ENGRAVING_PRICE_PER_PAIR} per pair, final sale)
                      </span>
                    </span>
                    <span className="block text-xs text-gray-500 mt-1" data-id="element-714">
                      Lasered items are final sale. Up to 20 characters per
                      name.
                    </span>
                  </span>
                </label>

                {engravingEnabled && <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-4" data-id="element-715">
                    {/* Mode radios */}
                    <div className="flex flex-col sm:flex-row gap-2" data-id="element-716">
                      <ModeRadio active={nameMode === 'same'} onClick={() => setNameMode('same')} label="Same name on all pairs" data-id="element-717" />
                      <ModeRadio active={nameMode === 'different'} onClick={() => setNameMode('different')} label="Different names per pair" data-id="element-718" />
                    </div>

                    {nameMode === 'same' && <div data-id="element-719">
                        <label htmlFor="same-name-input" className="block text-xs font-bold text-gray-700 mb-1.5" data-id="element-720">
                          Name to laser on all selected pairs
                        </label>
                        <input id="same-name-input" type="text" value={sameName} onChange={e => setSameName(e.target.value)} placeholder="Enter name (max 20 characters)" maxLength={20} className="w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-721" />
                      </div>}

                    {nameMode === 'different' && <div className="space-y-3" data-id="element-722">
                        {totalPairs === 0 ? <p className="text-xs text-gray-500 italic" data-id="element-723">
                            Select at least one pair above to enter names.
                          </p> : SIZES.filter(s => (quantities[s.value] || 0) > 0).map(s => <div key={s.value} data-id="element-724">
                              <p className="text-xs font-bold text-gray-700 mb-1.5" data-id="element-725">
                                {s.label}
                              </p>
                              <div className="space-y-1.5" data-id="element-726">
                                {Array.from({
                        length: quantities[s.value]
                      }).map((_, pairIdx) => <input key={pairIdx} type="text" value={perPairNames[s.value]?.[pairIdx] || ''} onChange={e => updatePerPairName(s.value, pairIdx, e.target.value)} placeholder={`Pair ${pairIdx + 1} — name`} maxLength={20} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-727" />)}
                              </div>
                            </div>)}
                      </div>}
                  </div>}
              </div>

              {/* Add to Cart */}
              <button type="button" disabled={totalPairs === 0} className="w-full bg-red-600 hover:bg-red-700 active:bg-red-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-bold text-base uppercase tracking-wide py-4 rounded-xl transition-colors shadow-sm hover:shadow-md focus:outline-none focus:ring-4 focus:ring-red-200" data-id="element-728">
                Add Selected Items To Cart
              </button>

              {/* Dimensions */}
              <div className="flex items-center justify-between mt-6 pt-5 border-t border-gray-100 text-sm" data-id="element-729">
                <span className="font-semibold text-gray-900" data-id="element-730">Dimensions</span>
                <span className="text-gray-600" data-id="element-731">12 x 4 x 2.5</span>
              </div>

              {/* Trust badges */}
              <div className="grid grid-cols-3 gap-3 mt-5 pt-5 border-t border-gray-100" data-id="element-732">
                <TrustItem icon={<StoreIcon className="w-4 h-4" data-id="element-734" />} label="Official U.S. Distributor" data-id="element-733" />
                <TrustItem icon={<ShieldCheckIcon className="w-4 h-4" data-id="element-736" />} label="Competition support" data-id="element-735" />
                <TrustItem icon={<TruckIcon className="w-4 h-4" data-id="element-738" />} label="Shipping available" data-id="element-737" />
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-16" data-id="element-739">
            <div className="border-b border-gray-200" data-id="element-740">
              <div className="flex gap-1 overflow-x-auto -mb-px" data-id="element-741">
                <TabButton active={activeTab === 'description'} onClick={() => setActiveTab('description')} data-id="element-742">
                  Description
                </TabButton>
                <TabButton active={activeTab === 'sizing'} onClick={() => setActiveTab('sizing')} data-id="element-743">
                  Size Chart
                </TabButton>
                <TabButton active={activeTab === 'related'} onClick={() => setActiveTab('related')} data-id="element-744">
                  Related Products
                </TabButton>
                <TabButton active={activeTab === 'shipping'} onClick={() => setActiveTab('shipping')} data-id="element-745">
                  Shipping / Returns
                </TabButton>
              </div>
            </div>

            <div className="bg-white rounded-b-2xl border border-t-0 border-gray-100 p-6 md:p-10" data-id="element-746">
              {activeTab === 'description' && <DescriptionPanel data-id="element-747" />}
              {activeTab === 'sizing' && <SizingPanel data-id="element-748" />}
              {activeTab === 'related' && <RelatedPanel data-id="element-749" />}
              {activeTab === 'shipping' && <ShippingPanel data-id="element-750" />}
            </div>
          </div>

          <div className="mt-10" data-id="element-751">
            <button type="button" onClick={() => window.history.back()} className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-red-600 transition-colors" data-id="element-752">
              <ChevronLeftIcon className="w-4 h-4" data-id="element-753" />
              Back to products
            </button>
          </div>
        </div>
      </main>

      <Footer data-id="element-754" />
    </div>;
}
/* ---------- Subcomponents ---------- */
function SizeRow({
  size,
  qty,
  onIncrement,
  onDecrement,
  onChange
}: {
  size: SizeOption;
  qty: number;
  onIncrement: () => void;
  onDecrement: () => void;
  onChange: (val: number) => void;
}) {
  const active = qty > 0;
  return <div className={`flex items-center justify-between gap-3 px-4 py-3 transition-colors ${active ? 'bg-red-50/40' : 'bg-white hover:bg-gray-50'}`} data-id="element-755">
      <div className="flex-1 min-w-0" data-id="element-756">
        <p className="text-sm font-semibold text-gray-900 leading-tight" data-id="element-757">
          {size.label}
        </p>
        <p className="text-xs text-gray-500" data-id="element-758">{size.range}</p>
      </div>
      <div className="inline-flex items-center border border-gray-300 rounded-lg overflow-hidden bg-white" data-id="element-759">
        <button type="button" onClick={onDecrement} disabled={qty === 0} className="px-2.5 py-1.5 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" aria-label={`Decrease ${size.label} quantity`} data-id="element-760">
          <MinusIcon className="w-4 h-4" data-id="element-761" />
        </button>
        <input type="number" min={0} value={qty} onChange={e => onChange(parseInt(e.target.value) || 0)} className="w-12 text-center font-semibold text-gray-900 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" aria-label={`${size.label} quantity`} data-id="element-762" />
        <button type="button" onClick={onIncrement} className="px-2.5 py-1.5 hover:bg-gray-100 transition-colors" aria-label={`Increase ${size.label} quantity`} data-id="element-763">
          <PlusIcon className="w-4 h-4" data-id="element-764" />
        </button>
      </div>
    </div>;
}
function ModeRadio({
  active,
  onClick,
  label
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return <button type="button" onClick={onClick} className={`flex-1 px-3 py-2.5 text-sm font-semibold rounded-lg border-2 transition-all ${active ? 'border-red-600 bg-white text-red-700' : 'border-transparent bg-white text-gray-700 hover:border-gray-300'}`} data-id="element-765">
      <span className="inline-flex items-center gap-2" data-id="element-766">
        <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${active ? 'border-red-600' : 'border-gray-400'}`} data-id="element-767">
          {active && <span className="w-2 h-2 rounded-full bg-red-600" data-id="element-768" />}
        </span>
        {label}
      </span>
    </button>;
}
function TrustItem({
  icon,
  label
}: {
  icon: React.ReactNode;
  label: string;
}) {
  return <div className="flex flex-col items-center text-center gap-1.5" data-id="element-769">
      <div className="text-gray-700" data-id="element-770">{icon}</div>
      <span className="text-[11px] font-medium text-gray-600 leading-tight" data-id="element-771">
        {label}
      </span>
    </div>;
}
function TabButton({
  active,
  onClick,
  children
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return <button type="button" onClick={onClick} className={`px-5 py-3 text-sm font-bold whitespace-nowrap border-b-2 transition-colors ${active ? 'border-red-600 text-red-600' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-772">
      {children}
    </button>;
}
function DescriptionPanel() {
  return <div className="prose max-w-none" data-id="element-773">
      <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-774">
        About the Gen3 E-Foot Gear
      </h3>
      <p className="text-gray-700 leading-relaxed mb-4" data-id="element-775">
        The Daedo Gen3 E-Foot Gear is the official sensor sock for Gen3
        electronic scoring. Designed for competition-level accuracy, the Gen3
        sock features additional sensors and improved detection for use with
        Gen3 E-Hogus and E-Headgear.
      </p>
      <p className="text-gray-700 leading-relaxed mb-4" data-id="element-776">
        Built for elite athletes and serious training environments, the Gen3
        E-Foot Gear provides robust, reliable scoring detection across practice
        and tournament play.
      </p>
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-6" data-id="element-777">
        <p className="text-sm text-amber-900" data-id="element-778">
          <span className="font-bold" data-id="element-779">Important:</span> Gen3 footgear is only
          compatible with Gen3 Electronic Protectors (E-Hogus). For Gen2
          systems, please use Gen2 footgear.
        </p>
      </div>
    </div>;
}
function SizingPanel() {
  return <div data-id="element-780">
      <div className="text-center mb-6" data-id="element-781">
        <h3 className="text-xl md:text-2xl font-black text-gray-900 uppercase tracking-tight" data-id="element-782">
          Don't Know Your Foot Size?
        </h3>
        <p className="text-base font-semibold text-gray-600 mt-1" data-id="element-783">
          E-Footgear Suggested Sizing Chart
        </p>
      </div>

      <SizingTable data={SIZING_TOP} data-id="element-784" />
      <div className="h-4" data-id="element-785" />
      <SizingTable data={SIZING_BOTTOM} data-id="element-786" />

      <div className="text-center text-sm text-gray-600 max-w-2xl mx-auto mt-6 bg-gray-50 rounded-xl p-4" data-id="element-787">
        <p data-id="element-788">
          <span className="font-semibold text-gray-900" data-id="element-789">
            This chart is a guideline.
          </span>{' '}
          Width, thickness and length of your foot should all be considered when
          purchasing e-footgear.
        </p>
      </div>
    </div>;
}
function SizingTable({
  data
}: {
  data: {
    header: string[];
    rows: string[][];
  };
}) {
  return <div className="overflow-x-auto rounded-xl border border-gray-200" data-id="element-790">
      <table className="min-w-full text-center text-xs sm:text-sm" data-id="element-791">
        <thead data-id="element-792">
          <tr className="bg-blue-50 text-blue-800" data-id="element-793">
            {data.header.map((h, i) => <th key={i} className="px-3 py-2.5 font-bold border-b border-blue-100 whitespace-nowrap" data-id="element-794">
                {h}
              </th>)}
          </tr>
        </thead>
        <tbody data-id="element-795">
          {data.rows.map((row, i) => <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'} data-id="element-796">
              {row.map((cell, j) => <td key={j} className={`px-3 py-2.5 whitespace-nowrap ${j === 0 ? 'font-semibold text-blue-700 text-left' : 'text-gray-700'}`} data-id="element-797">
                  {cell || '—'}
                </td>)}
            </tr>)}
        </tbody>
      </table>
    </div>;
}
function RelatedPanel() {
  return <div data-id="element-798">
      <h3 className="text-xl font-bold text-gray-900 mb-1" data-id="element-799">
        Other Suggested TrueScore Products
      </h3>
      <p className="text-sm text-gray-600 mb-6" data-id="element-800">
        Complete your Gen3 setup with these recommended pieces.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-801">
        {RELATED.map((product, idx) => <a key={idx} href={product.href} className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg hover:border-gray-300 transition-all" data-id="element-802">
            <div className="aspect-square bg-gray-50 flex items-center justify-center p-6 overflow-hidden" data-id="element-803">
              <img src={product.image} alt={product.name} className="max-w-full max-h-full object-contain transition-transform duration-300 group-hover:scale-105" data-id="element-804" />
            </div>
            <div className="p-4" data-id="element-805">
              <h4 className="font-bold text-gray-900 mb-1 text-base" data-id="element-806">
                {product.name}
              </h4>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-red-600 group-hover:text-red-700" data-id="element-807">
                View Product
                <ChevronRightIcon className="w-4 h-4" data-id="element-808" />
              </span>
            </div>
          </a>)}
      </div>
    </div>;
}
function ShippingPanel() {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-809">
      <div data-id="element-810">
        <div className="flex items-center gap-3 mb-3" data-id="element-811">
          <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center" data-id="element-812">
            <TruckIcon className="w-5 h-5 text-blue-700" data-id="element-813" />
          </div>
          <h3 className="text-lg font-bold text-gray-900" data-id="element-814">Shipping</h3>
        </div>
        <p className="text-sm text-gray-700 leading-relaxed" data-id="element-815">
          We ship competition gear across the U.S. Most orders are processed
          within 1-2 business days. Tracking is provided once your order ships.
        </p>
      </div>
      <div data-id="element-816">
        <div className="flex items-center gap-3 mb-3" data-id="element-817">
          <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center" data-id="element-818">
            <ShieldCheckIcon className="w-5 h-5 text-red-600" data-id="element-819" />
          </div>
          <h3 className="text-lg font-bold text-gray-900" data-id="element-820">Returns</h3>
        </div>
        <p className="text-sm text-gray-700 leading-relaxed" data-id="element-821">
          Unused, unopened gear may be returned within our standard return
          window.{' '}
          <span className="font-semibold text-red-600" data-id="element-822">
            Lasered/personalized items are final sale.
          </span>{' '}
          See our full{' '}
          <a href="#returns" className="text-blue-600 underline" data-id="element-823">
            return policy
          </a>{' '}
          for details.
        </p>
      </div>
    </div>;
}